/*
  Pagination arithmetic (§6.2, D23), as pure functions.

  Same page/rowsPerPage contract as the MUI TablePagination it replaces, so tasks 7 to 10
  switch by swapping the component. What it deliberately does NOT carry over is the old
  wrapper's URL writing: it pushed ?page= and ?perPage= on every page change and nothing
  ever read them back — the dead reader in helpers/tables.tsx had zero callers and went in
  task 6b, the writer in task 11 (D23). (Both are named in the plan and not spelled here,
  because task 11's done-when greps src for those symbols and expects nothing.)

  The one behaviour worth stating: the page is CLAMPED here rather than trusted. A filter
  that shrinks 132 rows to 3 while the visitor sits on page 7 would otherwise slice past
  the end and paint a blank table — the old code papered over the count === 0 case only
  (GameSetsTable.tsx:112, `page={count === 0 ? 0 : page}`) and still blanked for every
  other short result. Clamping in one place means no table has to remember.
*/

import { formatCount } from "lib";

/* The old pagination wrapper's options, kept unchanged. */
export const ROWS_PER_PAGE_OPTIONS: readonly number[] = [5, 10, 25, 50];

/* The one field of the old DEFAULT_TABLE_REDUCER_VALUES worth keeping; task 18 deleted the rest. */
export const DEFAULT_ROWS_PER_PAGE = 10;

export const pageCount = (count: number, rowsPerPage: number): number => {
  if (rowsPerPage <= 0) {
    return 0;
  }
  return Math.ceil(Math.max(count, 0) / rowsPerPage);
};

/* The page actually shown: never negative, never past the last page, always an integer. */
export const clampPage = (
  page: number,
  count: number,
  rowsPerPage: number
): number => {
  const pages = pageCount(count, rowsPerPage);
  if (pages === 0 || !Number.isFinite(page) || page < 0) {
    return 0;
  }
  return Math.min(Math.floor(page), pages - 1);
};

export const pageStartIndex = (
  page: number,
  count: number,
  rowsPerPage: number
): number => clampPage(page, count, rowsPerPage) * rowsPerPage;

/* The rows one page shows. rowsPerPage <= 0 means "no paging", not "no rows". */
export const pageRows = <TRow>(
  rows: readonly TRow[],
  page: number,
  rowsPerPage: number
): TRow[] => {
  if (rowsPerPage <= 0) {
    return rows.slice();
  }
  const start = pageStartIndex(page, rows.length, rowsPerPage);
  return rows.slice(start, start + rowsPerPage);
};

export const hasPreviousPage = (
  page: number,
  count: number,
  rowsPerPage: number
): boolean => clampPage(page, count, rowsPerPage) > 0;

export const hasNextPage = (
  page: number,
  count: number,
  rowsPerPage: number
): boolean =>
  clampPage(page, count, rowsPerPage) < pageCount(count, rowsPerPage) - 1;

/*
  "1–10 of 1,132". Through formatCount so the totals are thousands-separated by the same
  Intl.NumberFormat("en-US") every numeric cell uses (§6.2) — a bare toLocaleString here
  would print 1.132 for a de-DE visitor while the column beside it printed 1,132.
*/
export const pageRangeLabel = (
  page: number,
  count: number,
  rowsPerPage: number
): string => {
  const total = Math.max(count, 0);
  if (total === 0) {
    return "0 of 0";
  }
  const start = pageStartIndex(page, total, rowsPerPage);
  const end = rowsPerPage <= 0 ? total : Math.min(start + rowsPerPage, total);
  return `${formatCount(start + 1)}–${formatCount(end)} of ${formatCount(total)}`;
};
